import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';

import {
  getFormTemplateQueryKey,
  getFormTemplateOptions,
  updateTemplateMutation,
} from '@drivingdoc/api-client/generated/react-query';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import { RichTextEditorProvider } from 'components/Editor/RichTextEditor/RichTextEditorProvider';
import EmailTemplateForm, { EmailTemplateOutputValues } from 'components/TemplateForms';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

interface EmailTemplateProps {
  formId: string;
}

export function EmailTemplate({ formId }: EmailTemplateProps) {
  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const updateEmailTemplate = useMutation(updateTemplateMutation());

  // fetch the form
  const formInfo = useQuery({
    ...getFormTemplateOptions({
      path: {
        id: formId ?? '',
      },
    }),
    enabled: !!formId,
  });

  const onSubmit = async (data: EmailTemplateOutputValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm(`Update email template for "${formInfo.data?.name}"?`, {
        okText: 'Update',
        cancelText: 'Cancel',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      const result = await updateEmailTemplate.mutateAsync({
        path: {
          templateKey: 'form-issued',
          id: formId,
        },
        body: {
          email: data,
        },
      });

      if (result) {
        // invalidate the query key
        queryClient.invalidateQueries({
          queryKey: getFormTemplateQueryKey({
            path: {
              id: formId,
            },
          }),
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Email Template Updated',
        });
      }

      // const editorState = editor.getEditorState();
      // console.log(editorState.toJSON());
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: getApiErrorMessage(error),
      });
    }
  };
  return (
    <RichTextEditorProvider>
      <EmailTemplateForm templateKey="form-issued" id={formId} onSubmit={onSubmit} />
    </RichTextEditorProvider>
  );
}

export default EmailTemplate;
